/**
 * Segmented control: één keuze uit twee tot vier weergaven (review §6.2).
 *
 * Voor het wisselen tussen gelijkwaardige gezichtspunten op dezelfde inhoud --
 * Vragen | Overzicht, MUS | SRA -- waar losse knoppen de indruk gaven dat je
 * "wegging" in plaats van omschakelde. Toegankelijk als radiogroep.
 */

import * as React from "react";
import "./pane.css";

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: React.ReactNode;
  title?: string;
}

export interface SegmentedControlProps<T extends string> {
  options: Array<SegmentedOption<T>>;
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  compact?: boolean;
  /** Niet te wijzigen (bijv. tijdens een handeling); `disabledTitle` zegt waarom. */
  disabled?: boolean;
  disabledTitle?: string;
  className?: string;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  compact,
  disabled,
  disabledTitle,
  className,
}: SegmentedControlProps<T>): JSX.Element {
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    if (disabled) return;
    const i = options.findIndex((o) => o.value === value);
    const j = (i + (e.key === "ArrowRight" ? 1 : options.length - 1)) % options.length;
    onChange(options[j].value);
    // Focus mee naar de nieuwe keuze (roving tabindex): anders blijft hij op een knop met tabIndex -1.
    e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]')[j]?.focus();
  };
  return (
    <div
      className={`pane-segmented${compact ? " pane-segmented--compact" : ""}${className ? ` ${className}` : ""}`}
      role="radiogroup"
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      onKeyDown={onKeyDown}
    >
      {options.map((o) => {
        const checked = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            className="pane-segmented__option"
            title={disabled && disabledTitle ? disabledTitle : (o.title ?? o.label)}
            disabled={disabled && !checked}
            onClick={() => {
              if (!disabled) onChange(o.value);
            }}
          >
            {o.icon}
            <span>{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}
